import { useMemo, type ReactElement, type ReactNode } from 'react';
import { resolveTableMetricAccentColor } from '@/features/shared/utils/tableMetricPresentation';
import { getStatusCopy } from '@/shared/copy/status';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s, typographyTokens } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Box, useTheme } from '@/ui/primitives';
import { formatChartTooltipDateLabel } from '@/ui/primitives/chartDateLabel';
import {
  ChecklistRtlOutlinedIcon,
  FlakyIcon,
  NorthIcon,
  ReplayOutlinedIcon,
  SouthIcon,
  TimerOutlinedIcon,
} from '@/ui/primitives/icons';
import { MetricTrendCardFrame } from './MetricTrendCardFrame';
import { metricTrendCardCompactLayout } from './MetricTrendCardFrame.layout';
import { MetricTrendCardSkeleton } from './MetricTrendCardSkeleton';
import { MiniMetricLineChart } from './MiniMetricLineChart';
import type { MiniMetricChartSummary } from './MiniMetricChartHeader';
import type { MetricsStatusChartPanelDisplayModel } from './useMetricsStatusChartPanelModel';
import type { MiniMetricLineChartDatum } from '@/features/executions/utils/chartData';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';

type TrendDatum = {
  label: string;
  value: number | null;
};

type MetricTrendCardsRowProps = {
  loading: boolean;
  metricCards: MetricCardItem[];
  rangeLabel: string;
  deltaComparisonLabel: string;
  variant?: 'row' | 'stacked';
  includeTotalCard?: boolean;
  showFlakeScore?: boolean;
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  passRateChartData: MetricsStatusChartPanelDisplayModel['passRateChartData'];
  frequencyChartData: MetricsStatusChartPanelDisplayModel['frequencyChartData'];
  durationChartData: MetricsStatusChartPanelDisplayModel['durationChartData'];
  flakeScoreChartData: MetricsStatusChartPanelDisplayModel['flakeScoreChartData'];
  passRateSummary: MiniMetricChartSummary | null;
  frequencySummary: MiniMetricChartSummary | null;
  durationSummary: MiniMetricChartSummary | null;
  flakeScoreSummary: MiniMetricChartSummary | null | undefined;
};

type TrendDelta = {
  label: string | null;
  value: number | null;
  tone: 'positive' | 'negative' | 'neutral';
};

type TrendCardConfig = {
  id: string;
  chartKey: string;
  title: string;
  icon: TrendMetricIcon | null;
  thresholdTone: MiniMetricChartSummary['valueTone'] | null;
  valueLabel: string;
  delta: TrendDelta;
  data: TrendDatum[];
  metricType: 'count' | 'duration' | 'percentage';
  color: string;
  isMissing: boolean;
};

type TrendCardValueLabelParts = {
  primary: string;
  secondary: string | null;
};

type TrendMetricIcon = 'passRate' | 'frequency' | 'duration' | 'flakeScore';

const trendCardMinHeight = 112;
const trendCardChartHeight = 42;
const frequencySparklineColor = '#c9004f';
const flakeScoreSparklineColor = uiTokens.statusColors.orange;
const redThresholdAccentColor = '#ef625a';

const renderNodeLabel = (value: ReactNode): string => {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '-';
};

const isMissingLabel = (value: string | undefined): boolean => value === getStatusCopy('noData');

const buildTotalRunsTrend = (
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'],
): TrendDatum[] => {
  if (!statusChartData) return [];

  return statusChartData.data.map((row) => {
    const total = statusChartData.series.reduce((sum, seriesItem) => {
      const value = row[seriesItem.key];
      return sum + (typeof value === 'number' && Number.isFinite(value) ? value : 0);
    }, 0);

    return {
      label: String(row.label),
      value: total,
    };
  });
};

const buildLineTrend = (
  data: Array<Record<string, string | number | null>> | null,
  key: string,
): TrendDatum[] => {
  if (!data) return [];

  return data.map((row) => {
    const value = row[key];
    return {
      label: String(row.label),
      value: typeof value === 'number' && Number.isFinite(value) ? value : null,
    };
  });
};

const resolveMetricCardDelta = (card: MetricCardItem | undefined): TrendDelta => ({
  label: card
    ? renderNodeLabel(card.percentageDelta ?? card.countDelta ?? card.delta ?? null)
    : null,
  value: null,
  tone: card?.percentageDeltaTone ?? card?.countDeltaTone ?? card?.deltaTone ?? 'neutral',
});

const resolveSummaryDelta = (summary: MiniMetricChartSummary | null): TrendDelta => ({
  label: summary?.deltaLabel ?? null,
  value: summary?.deltaValue ?? null,
  tone: summary?.deltaTone ?? 'neutral',
});

const hasVisibleTrendValue = (data: TrendDatum[]): boolean =>
  data.some((point) => typeof point.value === 'number' && Number.isFinite(point.value));

const buildMiniMetricTrendData = (data: TrendDatum[]): MiniMetricLineChartDatum[] => {
  let carryForwardValue = 0;

  return data.map((point) => {
    if (typeof point.value === 'number' && Number.isFinite(point.value)) {
      carryForwardValue = point.value;
    }

    return {
      label: point.label,
      measuredValue: point.value,
      carryForwardValue,
    };
  });
};

const resolveYMax = (data: TrendDatum[], metricType: TrendCardConfig['metricType']): number => {
  if (metricType === 'percentage') return 106;

  const maxValue = data.reduce(
    (max, point) =>
      typeof point.value === 'number' && Number.isFinite(point.value) && point.value > max
        ? point.value
        : max,
    0,
  );
  if (maxValue <= 0) return 1;
  return maxValue * 1.18;
};

const resolveYMin = (data: TrendDatum[], metricType: TrendCardConfig['metricType']): number => {
  if (metricType === 'percentage') return -6;

  const values = data
    .map((point) => point.value)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (values.length === 0) return 0;

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = Math.max(1, maxValue - minValue);
  return Math.min(0, minValue - range * 0.16);
};

const resolveDeltaArrowDirection = (delta: TrendDelta): 'up' | 'down' => {
  if (typeof delta.value === 'number' && Number.isFinite(delta.value)) {
    return delta.value < 0 ? 'down' : 'up';
  }

  return delta.tone === 'negative' ? 'down' : 'up';
};

const splitFrequencyValueLabel = (value: string): TrendCardValueLabelParts => {
  const match = /^(.+?)\s+(\/\s*\S.*)$/.exec(value.trim());
  if (!match) return { primary: value, secondary: null };
  return {
    primary: match[1],
    secondary: match[2],
  };
};

const renderTrendMetricIcon = (icon: TrendMetricIcon | null): ReactElement | null => {
  const iconSx = {
    color: 'text.secondary',
    width: '19px !important',
    height: '19px !important',
  } as const;
  if (icon === 'passRate') {
    return <ChecklistRtlOutlinedIcon sx={[iconSx, { transform: 'translateY(1px)' }]} />;
  }
  if (icon === 'frequency') {
    return <ReplayOutlinedIcon sx={iconSx} />;
  }
  if (icon === 'duration') {
    return <TimerOutlinedIcon sx={[iconSx, { transform: 'translateY(1px)' }]} />;
  }
  if (icon === 'flakeScore') {
    return <FlakyIcon sx={[iconSx, { transform: 'translateY(1px)' }]} />;
  }
  return null;
};

const MetricTrendCard = ({
  config,
  loading,
  variant,
}: {
  config: TrendCardConfig;
  loading: boolean;
  variant: 'row' | 'stacked';
}): ReactElement => {
  const theme = useTheme();
  const isStacked = variant === 'stacked';
  const chartHeight = isStacked ? metricTrendCardCompactLayout.chartHeight : trendCardChartHeight;
  const cardMinHeight = isStacked ? metricTrendCardCompactLayout.minHeight : trendCardMinHeight;
  const horizontalPadding = isStacked ? metricTrendCardCompactLayout.headerPaddingX : s('lg');
  const titleFontSize = isStacked
    ? typographyTokens.body.fontSize
    : typographyTokens.caption.fontSize;
  const isMissingValue = config.isMissing || isMissingLabel(config.valueLabel);
  const valueFontSize = isMissingValue
    ? { xs: 16, xl: 18 }
    : isStacked
      ? { xs: 20, xl: 22 }
      : { xs: 20, xl: 23 };
  const deltaFontSize = typographyTokens.body.fontSize;
  const deltaColor =
    config.delta.tone === 'positive'
      ? resolveSemanticColor(theme, 'success')
      : config.delta.tone === 'negative'
        ? resolveSemanticColor(theme, 'error')
        : theme.app.colors.textMuted;
  const showDeltaIcon =
    config.delta.label !== null &&
    config.delta.label !== '-' &&
    config.delta.value !== 0 &&
    config.delta.tone !== 'neutral';
  const deltaArrowDirection = resolveDeltaArrowDirection(config.delta);
  const yMin = resolveYMin(config.data, config.metricType);
  const yMax = resolveYMax(config.data, config.metricType);
  const chartData = useMemo(() => buildMiniMetricTrendData(config.data), [config.data]);
  const hasData = isMissingValue ? false : hasVisibleTrendValue(config.data);
  const valueLabelParts =
    config.id === 'frequency'
      ? splitFrequencyValueLabel(config.valueLabel)
      : { primary: config.valueLabel, secondary: null };
  const valueColor = config.isMissing ? theme.app.colors.textMuted : theme.app.colors.text;
  const leftAccentColor =
    config.thresholdTone && !config.isMissing
      ? config.thresholdTone === 'red'
        ? redThresholdAccentColor
        : resolveTableMetricAccentColor(theme, config.thresholdTone)
      : undefined;

  if (loading) {
    return <MetricTrendCardSkeleton />;
  }

  const valueNode = (
    <Box
      sx={{
        minWidth: 0,
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: s('sm'),
      }}
    >
      <Box
        component="span"
        sx={{
          color: valueColor,
          fontSize: valueFontSize,
          fontWeight: typographyTokens.body.fontWeight,
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {valueLabelParts.primary}
      </Box>
      {valueLabelParts.secondary ? (
        <Box
          component="span"
          sx={{
            color: theme.app.colors.textMuted,
            fontSize: typographyTokens.body.fontSize,
            fontWeight: typographyTokens.caption.fontWeight,
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
          }}
        >
          {valueLabelParts.secondary}
        </Box>
      ) : null}
    </Box>
  );
  const chartNode = (() => {
    if (!hasData) {
      return (
        <Box
          sx={{
            flex: 1,
            minHeight: chartHeight,
            borderRadius: r('sm'),
            backgroundColor: theme.app.colors.surface,
          }}
        />
      );
    }

    return (
      <MiniMetricLineChart
        data={chartData}
        metricKey={config.chartKey}
        color={config.color}
        height={chartHeight}
        marginLeft={isStacked ? 0 : 2}
        marginRight={8}
        marginTop={isStacked ? 2 : 4}
        marginBottom={0}
        yAxisWidth={0}
        xAxisHeight={0}
        yAxisMin={yMin}
        yAxisMax={yMax}
        hideXAxisLabels
        hideYAxisLabels
        hideXAxisTicks
        hideYAxisTicks
        hideXAxisLine
        hideYAxisLine
        tooltipLabelFormatter={formatChartTooltipDateLabel}
      />
    );
  })();
  const footerNode = (
    <>
      {showDeltaIcon ? (
        deltaArrowDirection === 'down' ? (
          <SouthIcon sx={{ fontSize: 16, strokeWidth: 0.5 }} />
        ) : (
          <NorthIcon sx={{ fontSize: 16, strokeWidth: 0.5 }} />
        )
      ) : null}
      {config.delta.label ?? '-'}
    </>
  );

  return (
    <MetricTrendCardFrame
      title={config.title}
      cornerIcon={renderTrendMetricIcon(config.icon)}
      leftAccentColor={leftAccentColor}
      hideCornerIconBelowWidth={
        isStacked ? metricTrendCardCompactLayout.hideCornerIconBelowWidth : undefined
      }
      titleFontSize={titleFontSize}
      showInfoIcon={!isStacked}
      minHeight={cardMinHeight}
      chartHeight={chartHeight}
      fixedHeight={isStacked}
      headerPaddingX={horizontalPadding}
      headerPaddingTop={isStacked ? metricTrendCardCompactLayout.headerPaddingTop : s('lg')}
      headerPaddingBottom={isStacked ? metricTrendCardCompactLayout.headerPaddingBottom : s('xxs')}
      middleGridTemplateColumns={
        isStacked
          ? metricTrendCardCompactLayout.middleGridTemplateColumns
          : { xs: '1fr', md: 'max-content minmax(0, 1fr)' }
      }
      middleColumnGap={isStacked ? metricTrendCardCompactLayout.middleColumnGap : s('xs')}
      middlePaddingY={isStacked ? metricTrendCardCompactLayout.middlePaddingY : s('xs')}
      value={valueNode}
      valuePaddingX={isStacked ? metricTrendCardCompactLayout.valuePaddingX : horizontalPadding}
      valuePaddingRight={isStacked ? metricTrendCardCompactLayout.valuePaddingRight : undefined}
      valuePaddingY={isStacked ? metricTrendCardCompactLayout.valuePaddingY : s('xxs')}
      chart={chartNode}
      chartPaddingX={
        isStacked ? metricTrendCardCompactLayout.chartPaddingX : { xs: s('lg'), md: s('xs') }
      }
      chartPaddingLeft={
        isStacked ? metricTrendCardCompactLayout.chartPaddingLeft : { md: s('none') }
      }
      footer={footerNode}
      footerPaddingX={isStacked ? metricTrendCardCompactLayout.footerPaddingX : horizontalPadding}
      footerPaddingTop={isStacked ? metricTrendCardCompactLayout.footerPaddingTop : s('xs')}
      footerPaddingBottom={isStacked ? metricTrendCardCompactLayout.footerPaddingBottom : s('lg')}
      footerSx={{
        gridColumn: { xs: '1', md: '1 / -1' },
        gap: s('xs'),
        color: deltaColor,
        fontSize: deltaFontSize,
        fontWeight: typographyTokens.body.fontWeight,
        lineHeight: 1.2,
        fontVariantNumeric: 'tabular-nums',
      }}
    />
  );
};

export const MetricTrendCardsRow = ({
  loading,
  metricCards,
  rangeLabel,
  deltaComparisonLabel: _deltaComparisonLabel,
  variant = 'row',
  includeTotalCard = true,
  showFlakeScore = true,
  statusChartData,
  passRateChartData,
  frequencyChartData,
  durationChartData,
  flakeScoreChartData,
  passRateSummary,
  frequencySummary,
  durationSummary,
  flakeScoreSummary,
}: MetricTrendCardsRowProps): ReactElement => {
  const totalMetricCard = metricCards.find((card) => card.id === 'total');
  const titleSuffix = variant === 'stacked' ? '' : ` (${rangeLabel})`;
  const cards = useMemo<TrendCardConfig[]>(() => {
    const nextCards: TrendCardConfig[] = [
      {
        id: 'total-runs',
        chartKey: 'runs',
        title: `Total Runs${titleSuffix}`,
        icon: null,
        thresholdTone: null,
        valueLabel: renderNodeLabel(totalMetricCard?.count ?? '-'),
        delta: resolveMetricCardDelta(totalMetricCard),
        data: buildTotalRunsTrend(statusChartData),
        metricType: 'count',
        color: uiTokens.statusColors.teal,
        isMissing:
          totalMetricCard?.isMissing === true ||
          isMissingLabel(renderNodeLabel(totalMetricCard?.count)),
      },
      {
        id: 'success-rate',
        chartKey: 'passRate',
        title: `Success Rate${titleSuffix}`,
        icon: 'passRate',
        thresholdTone: passRateSummary?.valueTone ?? null,
        valueLabel: passRateSummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(passRateSummary),
        data: buildLineTrend(passRateChartData, 'passRate'),
        metricType: 'percentage',
        color: uiTokens.statusColors.green,
        isMissing:
          passRateSummary?.isMissing === true || isMissingLabel(passRateSummary?.valueLabel),
      },
      {
        id: 'frequency',
        chartKey: 'frequency',
        title: `Frequency${titleSuffix}`,
        icon: 'frequency',
        thresholdTone: frequencySummary?.valueTone ?? null,
        valueLabel: frequencySummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(frequencySummary),
        data: buildLineTrend(frequencyChartData, 'frequency'),
        metricType: 'count',
        color: frequencySparklineColor,
        isMissing:
          frequencySummary?.isMissing === true || isMissingLabel(frequencySummary?.valueLabel),
      },
      {
        id: 'average-duration',
        chartKey: 'duration',
        title: `Avg. Duration${titleSuffix}`,
        icon: 'duration',
        thresholdTone: durationSummary?.valueTone ?? null,
        valueLabel: durationSummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(durationSummary),
        data: buildLineTrend(durationChartData, 'duration'),
        metricType: 'duration',
        color: uiTokens.statusColors.purple,
        isMissing:
          durationSummary?.isMissing === true || isMissingLabel(durationSummary?.valueLabel),
      },
      {
        id: 'flake-score',
        chartKey: 'flakeScore',
        title: `Flake Score${titleSuffix}`,
        icon: 'flakeScore',
        thresholdTone: flakeScoreSummary?.valueTone ?? null,
        valueLabel: flakeScoreSummary?.valueLabel ?? '-',
        delta: resolveSummaryDelta(flakeScoreSummary ?? null),
        data: buildLineTrend(flakeScoreChartData ?? null, 'flakeScore'),
        metricType: 'percentage',
        color: flakeScoreSparklineColor,
        isMissing:
          flakeScoreSummary?.isMissing === true || isMissingLabel(flakeScoreSummary?.valueLabel),
      },
    ];

    return nextCards.filter((card) => {
      if (!includeTotalCard && card.id === 'total-runs') return false;
      if (!showFlakeScore && card.id === 'flake-score') return false;
      return true;
    });
  }, [
    durationChartData,
    durationSummary,
    flakeScoreChartData,
    flakeScoreSummary,
    frequencyChartData,
    frequencySummary,
    includeTotalCard,
    passRateChartData,
    passRateSummary,
    showFlakeScore,
    statusChartData,
    titleSuffix,
    totalMetricCard,
  ]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns:
          variant === 'stacked'
            ? {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'minmax(0, 1fr)',
              }
            : {
                xs: 'minmax(0, 1fr)',
                xl: 'repeat(5, minmax(0, 1fr))',
              },
        gap:
          variant === 'stacked'
            ? { xs: s('xl'), sm: s('xl'), lg: metricTrendCardCompactLayout.rowGap }
            : s('lg'),
      }}
    >
      {cards.map((card) => (
        <MetricTrendCard key={card.id} config={card} loading={loading} variant={variant} />
      ))}
    </Box>
  );
};
